import {SectionBgView} from '../ui_canvas_engine/SectionBgView'
import {GameStateEnums} from '../../../../shared/GameEnums'
import {UIGameManager} from './UIGameManager'
import {UIGameMechanic} from './UIGameEventController'
import {TimeUtils} from '../../../../shared/utils/TimeUtils'
import {UIGamePlayerAvatarView} from './ui_game_players/UIGamePlayerAvatarView'
import {TextTruncatedView} from '../ui_canvas_engine/TextTruncatedView'
import {TranslationObject} from '@colonist/i18n'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables'
import {ActionState, PlayerColor, TurnState} from '@colonist/game-states'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {SpriteView} from '../ui_canvas_engine/SpriteView'
import {ArrayUtils} from '@colonist/utils'

export abstract class UIGameStateController implements UIGameMechanic {
    protected readonly uiGameManager: UIGameManager
    protected container: SectionBgView

    protected stateText: TextTruncatedView
    protected singlePlayerAvatar: UIGamePlayerAvatarView
    protected multiPlayerIcon: SpriteView

    constructor(uiGameManager: UIGameManager) {
        this.uiGameManager = uiGameManager
        this.uiGameManager.eventController.registerUIGameMechanic(this)

        this.createContainer()
        this.connectedToGame()
    }

    protected abstract createContainer()

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.PlayerControllerStateUpdated:
            case UIGameEventEnums.TradeStateUpdated:
            case UIGameEventEnums.GameStateUpdated: this.update(); break
            case UIGameEventEnums.CanvasResized: this.onCanvasResize(); break
            case UIGameEventEnums.BeginnerModeStart: this.onBeginnerModeStart(); break
            default: break
        }
    }

    private onCanvasResize() {
        if(!this.container.visible) return
        this.container.destroy()
        this.singlePlayerAvatar.destroy()
        this.stateText.destroy()
        this.createContainer()
        this.update()
    }

    private onBeginnerModeStart() {
        this.container.visible = false
    }

    private connectedToGame() {
        if(this.uiGameManager.gameController.isReplay) {
            this.update()
            return
        }

        this.updateText({key: 'strings:game.gameState.connectedToGame'})
        this.showSinglePlayerAvatar(this.uiGameManager.gameController.myColor)
        const delayBeforeDisplayingTurnState = 3 * TimeUtils.MILLISECONDS
        setTimeout(() => {
            this.update()
        }, delayBeforeDisplayingTurnState)
    }

    private update() {
        const amITakingAction = this.uiGameManager.gameController.amITakingAction()
        const playersTakingAction = this.uiGameManager.gameController.getPlayersTakingAction()
        this.updatePlayerAvatar(amITakingAction, playersTakingAction)
        this.updateGameStateMessage(amITakingAction, playersTakingAction)
    }

    private updatePlayerAvatar(amITakingAction: boolean, playersTakingAction: PlayerColor[]) {
        if(playersTakingAction.length == 0) {
            this.showSinglePlayerAvatar(this.uiGameManager.gameController.currentState.currentTurnPlayerColor)
            return
        }

        if(playersTakingAction.length == 1) {
            this.showSinglePlayerAvatar(playersTakingAction[0])
            return
        }

        amITakingAction ? this.showSinglePlayerAvatar(this.uiGameManager.gameController.myColor) : this.showMultiplayerIcon()
    }

    private showMultiplayerIcon() {
        if(this.singlePlayerAvatar.parent != undefined) this.singlePlayerAvatar.parent.removeChild(this.singlePlayerAvatar)
        this.container.addChild(this.multiPlayerIcon)
    }

    private showSinglePlayerAvatar(player: PlayerColor) {
        const playerState = this.uiGameManager.gameController.getPlayerState(player)
        if(this.multiPlayerIcon.parent != undefined) this.multiPlayerIcon.parent.removeChild(this.multiPlayerIcon)
        this.container.addChild(this.singlePlayerAvatar)
        void this.singlePlayerAvatar.updatePlayerColor(playerState)
    }

    private updateGameStateMessage(amITakingAction: boolean, playersTakingAction: PlayerColor[]) {
        const isActionStateNone = this.uiGameManager.gameController.currentState.actionState == ActionState.None
        const message = isActionStateNone ? this.getTurnStateMessage(amITakingAction, playersTakingAction) : this.getActionStateMessage(amITakingAction, playersTakingAction)

        this.updateText(message)
    }

    protected updateText(text: TranslationObject) {
        const isDiscord = UIUtilsEjsVariables.isUserOnDiscord
        const options = isDiscord ? {...text.options, interpolation: {escapeValue: false}} : text.options
        const message: TranslationObject = {key: text.key, options: options}
        this.stateText.setText(message)
        this.stateText.centerX()
    }

    private getActionStateMessage(amITakingAction: boolean, playersTakingAction: PlayerColor[]): TranslationObject {
        if(this.uiGameManager.gameController.currentState.actionState == ActionState.None) return {key: 'strings:colonistCommon.utils.emptyString'}

        const numberOfPlayersTakingAction = playersTakingAction.length
        const playerDisplayUsername = this.getPlayerDisplayUsername(playersTakingAction)

        switch(this.uiGameManager.gameController.currentState.actionState) {
            case ActionState.InitialPlacementPlaceSettlement:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.initialPlacementPlaceSettlementSelf'} :
                    {key: 'strings:game.gameState.actions.initialPlacementPlaceSettlementOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.InitialPlacementPlaceCity:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.initialPlacementPlaceCitySelf'} :
                    {key: 'strings:game.gameState.actions.initialPlacementPlaceCityOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.InitialPlacementRoadPlacement:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.initialPlacementRoadPlacementSelf', options: {context: this.uiGameManager.gameState.gameModeData.translationKeyContext}} :
                    {key: 'strings:game.gameState.actions.initialPlacementRoadPlacementOther', options: {context: this.uiGameManager.gameState.gameModeData.translationKeyContext, playerUsername: playerDisplayUsername}}
            case ActionState.PlaceRoad:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeRoadSelf'} :
                    {key: 'strings:game.gameState.actions.placeRoadOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.PlaceSettlement:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeSettlementSelf'} :
                    {key: 'strings:game.gameState.actions.placeSettlementOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.PlaceCity:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeCitySelf'} :
                    {key: 'strings:game.gameState.actions.placeCityOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.PlaceShip:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeShipSelf'} :
                    {key: 'strings:game.gameState.actions.placeShipOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.SelectShipToMove:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectShipToMoveSelf'} :
                    {key: 'strings:game.gameState.actions.selectShipToMoveOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.MoveShip:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.moveShipSelf'} :
                    {key: 'strings:game.gameState.actions.moveShipOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.PlaceCityWall:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeCityWallSelf'} :
                    {key: 'strings:game.gameState.actions.placeCityWallOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.PlaceKnight:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeKnightSelf'} :
                    {key: 'strings:game.gameState.actions.placeKnightOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.UpgradeKnight:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.upgradeKnightSelf'} :
                    {key: 'strings:game.gameState.actions.upgradeKnightOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.ActivateKnight:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.activateKnightSelf'} :
                    {key: 'strings:game.gameState.actions.activateKnightOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.SelectKnightToTakeAction:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectKnightToTakeActionSelf'} :
                    {key: 'strings:game.gameState.actions.selectKnightToTakeActionOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.SelectKnightAction:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectKnightActionSelf'} :
                    {key: 'strings:game.gameState.actions.selectKnightActionOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.PlaceRoadForFree:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeRoadForFreeSelf'} :
                    {key: 'strings:game.gameState.actions.placeRoadForFreeOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.PlaceShipForFree:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeShipForFreeSelf'} :
                    {key: 'strings:game.gameState.actions.placeShipForFreeOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.PlaceCityWithDiscount:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeCityWithDiscountSelf'} :
                    {key: 'strings:game.gameState.actions.placeCityWithDiscountOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.PlaceCityWallForFree:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeCityWallForFreeSelf'} :
                    {key: 'strings:game.gameState.actions.placeCityWallForFreeOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.PlaceKnightForFree:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeKnightForFreeSelf'} :
                    {key: 'strings:game.gameState.actions.placeKnightForFreeOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.UpgradeKnightForFree:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.upgradeKnightForFreeSelf'} :
                    {key: 'strings:game.gameState.actions.upgradeKnightForFreeOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.MoveKnightOffTurn:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.moveKnightOffTurnSelf'} :
                    {key: 'strings:game.gameState.actions.moveKnightOffTurnOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.SelectKnightToRemove:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectKnightToRemoveSelf'} :
                    {key: 'strings:game.gameState.actions.selectKnightToRemoveOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.PlaceRobberOnly:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeRobberOnlySelf'} :
                    {key: 'strings:game.gameState.actions.placeRobberOnlyOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.PlacePirateOnly:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placePirateOnlySelf'} :
                    {key: 'strings:game.gameState.actions.placePirateOnlyOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.PlaceRobberOrPirate:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeRobberOrPirateSelf', options: {context: this.uiGameManager.gameState.gameModeData.translationKeyContext}} :
                    {key: 'strings:game.gameState.actions.placeRobberOrPirateOther', options: {context: this.uiGameManager.gameState.gameModeData.translationKeyContext, playerUsername: playerDisplayUsername}}
            case ActionState.SelectWhoToRob:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectWhoToRobSelf'} :
                    {key: 'strings:game.gameState.actions.selectWhoToRobOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.SelectCardsToDiscard:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectCardsToDiscardSelf', options: {count: numberOfPlayersTakingAction}} :
                    {key: 'strings:game.gameState.actions.selectCardsToDiscardOther', options: {count: numberOfPlayersTakingAction, playerUsername: playerDisplayUsername}}
            case ActionState.SelectProgressCardsToDiscard:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectProgressCardsToDiscardSelf', options: {count: numberOfPlayersTakingAction}} :
                    {key: 'strings:game.gameState.actions.selectProgressCardsToDiscardOther', options: {count: numberOfPlayersTakingAction, playerUsername: playerDisplayUsername}}
            case ActionState.Place2MoreRoadBuilding:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.place2MoreRoadBuildingSelf'} :
                    {key: 'strings:game.gameState.actions.place2MoreRoadBuildingOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.Place1MoreRoadBuilding:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.place1MoreRoadBuildingSelf'} :
                    {key: 'strings:game.gameState.actions.place1MoreRoadBuildingOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.Select2ResourcesForYearOfPlenty:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.select2ResourcesForYearOfPlentySelf'} :
                    {key: 'strings:game.gameState.actions.select2ResourcesForYearOfPlentyOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.Select1ResourceForMonopoly:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.select1ResourceForMonopolySelf'} :
                    {key: 'strings:game.gameState.actions.select1ResourceForMonopolyOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.SelectCardsForPendingDistributions:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectCardsForPendingDistributionsSelf', options: {count: numberOfPlayersTakingAction}} :
                    {key: 'strings:game.gameState.actions.selectCardsForPendingDistributionsOther', options: {count: numberOfPlayersTakingAction, playerUsername: playerDisplayUsername}}
            case ActionState.PlaceMetropolis:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeMetropolisSelf'} :
                    {key: 'strings:game.gameState.actions.placeMetropolisOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.SelectProgressCard:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectProgressCardSelf', options: {count: numberOfPlayersTakingAction}} :
                    {key: 'strings:game.gameState.actions.selectProgressCardOther', options: {count: numberOfPlayersTakingAction, playerUsername: playerDisplayUsername}}
            case ActionState.SelectCityToPillage:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectCityToPillageSelf'} :
                    {key: 'strings:game.gameState.actions.selectCityToPillageOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.PlaceMerchant:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.placeMerchantSelf'} :
                    {key: 'strings:game.gameState.actions.placeMerchantOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.SelectDiceForAlchemist:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectDiceForAlchemistSelf'} :
                    {key: 'strings:game.gameState.actions.selectDiceForAlchemistOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.SelectTileProductionNumbersToSwap:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectTileProductionNumbersToSwapSelf'} :
                    {key: 'strings:game.gameState.actions.selectTileProductionNumbersToSwapOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.SelectPlayerForDeserter:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectPlayerForDeserterSelf'} :
                    {key: 'strings:game.gameState.actions.selectPlayerForDeserterOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.SelectRoadToRemove:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectRoadToRemoveSelf'} :
                    {key: 'strings:game.gameState.actions.selectRoadToRemoveOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.IntrigueSelectKnightToMove:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.intrigueSelectKnightToMoveSelf'} :
                    {key: 'strings:game.gameState.actions.intrigueSelectKnightToMoveOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.SpySelectPlayer:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.spySelectPlayerSelf'} :
                    {key: 'strings:game.gameState.actions.spySelectPlayerOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.SpySelectProgressCard:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.spySelectProgressCardSelf'} :
                    {key: 'strings:game.gameState.actions.spySelectProgressCardOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.SelectCardsForWedding:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.selectCardsForWeddingSelf', options: {count: numberOfPlayersTakingAction}} :
                    {key: 'strings:game.gameState.actions.selectCardsForWeddingOther', options: {count: numberOfPlayersTakingAction, playerUsername: playerDisplayUsername}}
            case ActionState.CommercialHarborSelectPlayerAndResource:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.commercialHarborSelectPlayerAndResourceSelf'} :
                    {key: 'strings:game.gameState.actions.commercialHarborSelectPlayerAndResourceOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.CommercialHarborOpponentSelectsCard:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.commercialHarborOpponentSelectsCardSelf'} :
                    {key: 'strings:game.gameState.actions.commercialHarborOpponentSelectsCardOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.MasterMerchantSelectPlayer:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.masterMerchantSelectPlayerSelf'} :
                    {key: 'strings:game.gameState.actions.masterMerchantSelectPlayerOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.MasterMerchantSelectCards:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.masterMerchantSelectCardsSelf'} :
                    {key: 'strings:game.gameState.actions.masterMerchantSelectCardsOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.MerchantFleetSelectResource:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.merchantFleetSelectResourceSelf'} :
                    {key: 'strings:game.gameState.actions.merchantFleetSelectResourceOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.ResourceMonopolySelectResource:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.resourceMonopolySelectResourceSelf'} :
                    {key: 'strings:game.gameState.actions.resourceMonopolySelectResourceOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.CommodityMonopolySelectResource:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.commodityMonopolySelectResourceSelf'} :
                    {key: 'strings:game.gameState.actions.commodityMonopolySelectResourceOther', options: {playerUsername: playerDisplayUsername}}
            case ActionState.DiplomatSelectWhetherToPlaceNewEdgePiece:
                return amITakingAction ?
                    {key: 'strings:game.gameState.actions.diplomatSelectWhetherToPlaceNewEdgePieceSelf'} :
                    {key: 'strings:game.gameState.actions.diplomatSelectWhetherToPlaceNewEdgePieceOther', options: {playerUsername: playerDisplayUsername}}
        }
    }

    private getTurnStateMessage(amITakingAction: boolean, playersTakingAction: PlayerColor[]): TranslationObject {
        const playerDisplayUsername = this.getPlayerDisplayUsername(playersTakingAction)

        switch(this.uiGameManager.gameController.currentState.turnState) {
            case TurnState.InitialPlacement:
                return amITakingAction ?
                    {key: 'strings:game.gameState.turn.initialPlacementSelf'} :
                    {key: 'strings:game.gameState.turn.initialPlacementOther', options: {playerUsername: playerDisplayUsername}}
            case TurnState.Dice:
                return amITakingAction ?
                    {key: 'strings:game.gameState.turn.diceSelf'} :
                    {key: 'strings:game.gameState.turn.diceOther', options: {playerUsername: playerDisplayUsername}}
            case TurnState.Turn: return this.getTurnStateTurnMessage(amITakingAction, playersTakingAction)
            case TurnState.SpecialBuildPhase:
                return amITakingAction ?
                    {key: 'strings:game.gameState.turn.specialBuildPhaseSelf'} :
                    {key: 'strings:game.gameState.turn.specialBuildPhaseOther', options: {playerUsername: playerDisplayUsername}}
            case TurnState.GameEnd:
                return amITakingAction ?
                    {key: 'strings:game.gameState.turn.gameEndSelf'} :
                    {key: 'strings:game.gameState.turn.gameEndOther', options: {playerUsername: playerDisplayUsername}}
        }
    }

    private getTurnStateTurnMessage(amITakingAction: boolean, playersTakingAction: PlayerColor[]): TranslationObject {
        const playerDisplayUsername = this.getPlayerDisplayUsername(playersTakingAction)
        const numberOfPlayersTakingAction = playersTakingAction.length

        const arePlayersTrading = Object.keys(this.uiGameManager.gameController.tradeState.activeOffers).length > 0

        if(arePlayersTrading) {
            const amICurrentTurnPlayer = this.uiGameManager.gameController.currentState.currentTurnPlayerColor == this.uiGameManager.gameController.myColor

            if(amICurrentTurnPlayer) {
                const otherPlayersTrading = [...playersTakingAction]
                ArrayUtils.removeElementFromArray(otherPlayersTrading, this.uiGameManager.gameController.currentState.currentTurnPlayerColor)
                const numberOtherPlayersTrading = otherPlayersTrading.length
                const otherPlayerDisplayName = this.getPlayerDisplayUsername(otherPlayersTrading)

                const wasATradeAccepted = this.uiGameManager.gameController.wasATradeAccepted()

                wasATradeAccepted ? this.showSinglePlayerAvatar(this.uiGameManager.gameController.currentState.currentTurnPlayerColor) : this.updatePlayerAvatar(wasATradeAccepted, otherPlayersTrading)

                return wasATradeAccepted ?
                    {key: 'strings:game.gameState.turn.tradingSelf', options: {count: numberOtherPlayersTrading}} :
                    {key: 'strings:game.gameState.turn.tradingOther', options: {count: numberOtherPlayersTrading, playerUsername: otherPlayerDisplayName}}
            } else {
                return amITakingAction ?
                    {key: 'strings:game.gameState.turn.tradingSelf', options: {count: numberOfPlayersTakingAction}} :
                    {key: 'strings:game.gameState.turn.tradingOther', options: {count: numberOfPlayersTakingAction, playerUsername: playerDisplayUsername}}
            }
        }

        return amITakingAction ?
            {key: 'strings:game.gameState.turn.turnSelf'} :
            {key: 'strings:game.gameState.turn.turnOther', options: {playerUsername: playerDisplayUsername}}
    }

    private getPlayerDisplayUsername(playersTakingAction: PlayerColor[]): string {
        if(playersTakingAction.length == 0) {
            const currentTurnPlayer = this.uiGameManager.gameController.getPlayerState(this.uiGameManager.gameController.currentState.currentTurnPlayerColor)
            return currentTurnPlayer.username
        }

        if(playersTakingAction.length == 1) {
            const playerState = this.uiGameManager.gameController.getPlayerState(playersTakingAction[0])
            return playerState.username
        }

        return 'Players'
    }
}
